import { Flex, Text } from '@chakra-ui/react';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { useBrand, useUser } from 'modules/User';
import { BrandOptionDropdown } from 'modules/User/components/BrandOptionDropdown';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NewWidget } from 'widgets/Widget.types';

import { BrandKitFonts } from '../ApplyFonts/ApplyFonts';
import { getBrandColorPalette, serializeLogoData } from '../BrandKitMenu.helper';
import { EditBrandKitButton } from '../EditBrandKitButton';
import { AddBrandKitButton } from './AddBrandKitButton';
import { BrandLogos } from './BrandLogos';
import { BrandPalettes } from './BrandPalettes';
import { Loading } from './Loading';
import { SimpleBrandKitButton } from './SimpleEditBrandKitButton';

export interface BrandKitTabProps {
  onLogoClick: (widget: NewWidget | NewWidget[]) => void;
  handleShuffleColors?: (colors: string[], isBrandKit?: boolean) => void;
  disableDragDrop?: boolean;

  // If FALSE, hide the color palettes
  isReplaceMenu?: boolean;

  onOpenUpgradeModal?: () => void;
}

export const BrandKitTab = memo(
  ({
    onLogoClick,
    disableDragDrop,
    handleShuffleColors,
    isReplaceMenu = false,
    onOpenUpgradeModal,
  }: BrandKitTabProps) => {
    const {
      isFreeUser,
      isPremiumUser,
      isGuestRole,
      isEnterpriseUser,
      isBusinessUser,
      isTeamAdminRole,
      isTeamOwnerRole,
      isAdminUser,
    } = useUser();
    const { venngageE2 } = useVenngageE1Gate();
    const { data: brandData } = useBrand();
    const brandIdx = useAppSelector(selectBrandIdx);
    const brandLogos = brandData?.[brandIdx]?.logos ?? [];

    const { brandConfig = { editable: undefined } } = useBrand({
      brandId: brandData?.[brandIdx]?.id,
    });

    const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });

    const serializedLogoData = serializeLogoData({ data: brandLogos });

    // Note: Those users with unGatedBrandKit experience can use the brand kit
    const hasUnGatedBrandKit = (isFreeUser || isPremiumUser) && !!venngageE2?.experiments?.unGatedBrandKit;

    // Free and Premium users should always not have the brand dropdown. other user types depends on the number of brands.
    const showBrandKitDropdown = brandData;

    // Free and Premium users should not be able to use but see the logo's with business badge labeled.
    const hasLogoPermission = (!isFreeUser && !isPremiumUser) || hasUnGatedBrandKit;

    const hasPaletteFontPermission = (!isFreeUser && !isPremiumUser && !isGuestRole) || hasUnGatedBrandKit;

    // Free and Premium has the permission to edit their brand because they are the owner.
    // Enterprise/Business admin users should have the permission to edit their brand. However, Members and Guests are not.
    const canEditBrandKit =
      (isBusinessUser && isTeamOwnerRole) ||
      (isEnterpriseUser && isTeamAdminRole) ||
      isFreeUser ||
      isPremiumUser ||
      isAdminUser;
    const hasBrandEditPermission =
      isFreeUser || isPremiumUser || (brandConfig.editable === undefined ? false : brandConfig.editable);

    // Determine if showing the logo view. if no logos set on E1, use the NoLogoView to gate them
    const showLogos = !!serializedLogoData.length;

    const brandColorPalettes = useMemo(() => {
      return brandData && brandData.length > 0 ? getBrandColorPalette(brandData[brandIdx]) : [];
    }, [brandIdx, brandData]);

    const hasColorPalettes = brandData && brandColorPalettes.length > 0;
    const hasNoBrand = !hasColorPalettes && !showLogos;

    // free enterprise users should not get the autobrand modal
    if (!canEditBrandKit && hasNoBrand) return null;

    const isLoading = brandConfig.editable === undefined && !!brandData?.length;

    return (
      <Flex direction='column' h='full' padding={4} paddingTop={4}>
        <Flex flexDir='column' h='fit-content' flexGrow={1}>
          {showBrandKitDropdown && (
            <Flex justifyContent='space-between' alignItems='center' mb={4} gap={2}>
              <BrandOptionDropdown w='156px' triggerProps={{ h: 8, w: '156px' }} enableIfOnlyOneBrand />
              {canEditBrandKit && <AddBrandKitButton hasEditPermission={hasLogoPermission} ariaLabel='' />}
            </Flex>
          )}

          <Flex flexDir='column' flexGrow={1} overflow='auto' mb={4}>
            {!isReplaceMenu && (
              <BrandPalettes
                hasBrandKitPermission={hasPaletteFontPermission}
                brandColorPalettes={brandColorPalettes}
                handleShuffleColors={handleShuffleColors}
                hasEditBrandKitPermission={hasBrandEditPermission}
                isLoading={isLoading}
              />
            )}
            <BrandLogos
              hasBrandEditPermission={hasBrandEditPermission}
              serializedLogoData={serializedLogoData}
              onLogoClick={onLogoClick}
              hasLogoPermission={hasLogoPermission}
              disableDragDrop={disableDragDrop}
              // Don't show logo heading in replace menu
              showHeading={!isReplaceMenu}
              // Show separate view for no logos in replace menu
              showNoLogoView={isReplaceMenu}
              isLoading={isLoading}
            />
            {!isReplaceMenu && (
              <>
                <Flex alignItems='center' justifyContent='space-between' mt={4} mb={4}>
                  <Text fontSize='sm' fontWeight='semibold' color='font.500'>
                    {t('fontsLabel')}
                  </Text>
                  <SimpleBrandKitButton ariaLabel={t('fontsAriaLabel')} hasEditPermission={hasBrandEditPermission} />
                </Flex>
                {isLoading && <Loading />}
                {!isLoading && (
                  <BrandKitFonts
                    onOpenUpgradeModal={onOpenUpgradeModal!}
                    hasBrandKitPermission={hasPaletteFontPermission}
                    hasBrandKitEditPermission={hasBrandEditPermission}
                  />
                )}
              </>
            )}
          </Flex>
        </Flex>
        {isReplaceMenu && hasBrandEditPermission && <EditBrandKitButton marginX={0} mb={2} mt={4} flexShrink={0} />}
      </Flex>
    );
  },
);

BrandKitTab.displayName = 'BrandKitTab';
